import {
  flattenPlaybookTreeWithDepth,
  isPlaybookSectionNode,
  type PlaybookNodeId,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import type {
  FeedbackWithCurrentGrade,
  RecordingAssessmentFeedbackStatus,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@repo/ui/toggleButton/segmentedControl";
import { type FC, useMemo } from "react";
import { FeedbackReviewNavigation } from "./feedbackReviewNavigation.js";
import { FeedbackReviewNodePanel } from "./feedbackReviewNodePanel.js";
import { FeedbackReviewTable } from "./feedbackReviewTable.js";
import {
  buildPlaybookOpenFeedbackCounts,
  getInitialFeedbackReviewNodeId,
  getPlaybookOpenFeedbackPosition,
} from "./feedbackReviewTreeHelpers.js";

const feedbackStatusOptions: readonly SegmentedControlOption<RecordingAssessmentFeedbackStatus>[] =
  [
    { id: "open", label: "Open" },
    { id: "resolved", label: "Resolved" },
    { id: "outdated", label: "Outdated" },
  ];

export interface PlaybookFeedbackReviewTabProps {
  /** Every feedback row for the playbook, in server order. */
  feedback: readonly FeedbackWithCurrentGrade[];
  /** True while the feedback list request is still in flight. */
  isFeedbackLoading: boolean;
  /** True when the feedback list request failed. */
  isFeedbackError: boolean;
  /** The live tree — feedback is graded against live, not the draft. */
  tree: PlaybookTree;
  /** The URL-resolved review target. */
  pickedNodeId: PlaybookNodeId | null;
  onPickNode: (nodeId: PlaybookNodeId) => void;
  status: RecordingAssessmentFeedbackStatus;
  onStatusChange: (status: RecordingAssessmentFeedbackStatus) => void;
}

/**
 * The playbook editor's "Review feedback" tab: one criterion (or section) at a
 * time, its grading instructions on the left and the feedback reviewers left on
 * it on the right.
 */
export const PlaybookFeedbackReviewTab: FC<PlaybookFeedbackReviewTabProps> = (
  props,
) => {
  const {
    feedback,
    isFeedbackLoading,
    isFeedbackError,
    tree,
    pickedNodeId,
    onPickNode,
    status,
    onStatusChange,
  } = props;

  const flattenedNodes = useMemo(
    () => flattenPlaybookTreeWithDepth(tree),
    [tree],
  );
  const openFeedbackCounts = useMemo(
    () => buildPlaybookOpenFeedbackCounts(feedback),
    [feedback],
  );

  const initialSelectedNodeId = useMemo(
    () => getInitialFeedbackReviewNodeId(flattenedNodes, openFeedbackCounts),
    [flattenedNodes, openFeedbackCounts],
  );

  // Keep the existing defensive fallback for callers resolving against a tree
  // that changes between the URL-state pass and this render.
  const selectedEntry =
    flattenedNodes.find((entry) => entry.node.id === pickedNodeId) ??
    flattenedNodes.find((entry) => entry.node.id === initialSelectedNodeId);
  const selectedNodeId = selectedEntry?.node.id ?? null;

  const feedbackItems = useMemo(
    () =>
      feedback.filter(
        (item) =>
          item.criteria_node_id === selectedNodeId && item.status === status,
      ),
    [feedback, selectedNodeId, status],
  );

  // Where the tab lands depends on the feedback, so nothing sensible can show
  // until it arrives — rendering early would flash the first criterion and
  // then move to the first node with open feedback.
  if (isFeedbackLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader style={LoaderStyle.SPINNER} />
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-cols-[minmax(300px,380px)_minmax(0,1fr)] gap-4 p-4">
      <FeedbackReviewNodePanel
        node={selectedEntry?.node}
        openFeedbackPosition={getPlaybookOpenFeedbackPosition(
          flattenedNodes,
          openFeedbackCounts,
          selectedNodeId,
        )}
      />

      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex shrink-0 items-center gap-3">
          <SegmentedControl
            aria-label="Feedback status"
            size={Size.SMALL}
            options={feedbackStatusOptions}
            value={status}
            onChange={onStatusChange}
          />
          <div className="flex-1" />
          <FeedbackReviewNavigation
            flattenedNodes={flattenedNodes}
            openFeedbackCounts={openFeedbackCounts}
            selectedNodeId={selectedNodeId}
            onSelectNode={onPickNode}
          />
        </div>

        {/* The table owns its own scrolling, so bound its height here. */}
        <div className="flex min-h-0 flex-1 flex-col">
          <FeedbackReviewTable
            feedbackItems={feedbackItems}
            isLoading={isFeedbackLoading}
            isError={isFeedbackError}
            isSectionSelected={
              selectedEntry !== undefined &&
              isPlaybookSectionNode(selectedEntry.node)
            }
            status={status}
          />
        </div>
      </div>
    </div>
  );
};
